import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogTitle,
  IconButton,
  Typography,
} from "@mui/material";
import { IDetectedBarcode } from "@yudiel/react-qr-scanner";
import { FC, useState } from "react";
import QrScanner from "../QrScanner";
import { AddProduct } from "../../../../api/inventory";
import { useInventory } from "../../InventoryProvider";
import { CloseRounded } from "@mui/icons-material";
import { ErrorCodes, Severity } from "../../../../types/enums";
import { useApplicationContext } from "../../../ContextProviders/ApplicationContext";
import { useTranslation } from "react-i18next";

type QrScannerFindType = {
  onScannedListChange: (
    listOfScannedQr: string[],
    QrValueJustScanned: string
  ) => void;
  qrScannerToggle: boolean;
  setQrScannerToggleFalse: () => void;
};

const QrScannerFind: FC<QrScannerFindType> = ({
  onScannedListChange,
  qrScannerToggle,
  setQrScannerToggleFalse,
}) => {
  const [listOfScannedQr, setListOfScannedQr] = useState<string[]>([]);
  const [scanningError, setScanningError] = useState<{
    error: string | null;
    severity: Severity | null;
    serial?: string;
  }>({
    error: null,
    severity: null,
    serial: "",
  });
  const { t } = useTranslation();
  const { rows } = useInventory();
  const { showSnackbar } = useApplicationContext();

  const errorString = {
    noExist: `${t("inventory.productDoesNotExist")}`,
    alreadyFound: `${t("inventory.productAlreadyFoundAndSelected")}`,
    new: `${t("inventory.productFoundAndSelected")}`,
  };

  const handleScan = (result: IDetectedBarcode[]) => {
    //QR Codes on vests has a space every 4 numbers. We remove that.
    const qrValue = result[0].rawValue.replaceAll(" ", "");

    const productExists = rows.some((row) => row.serial === qrValue);
    if (!productExists) {
      setScanningError({
        error: errorString.noExist,
        severity: "warning",
        serial: qrValue,
      });
      return;
    }

    setListOfScannedQr((prev) => {
      const alreadyScanned = prev.includes(qrValue);

      if (alreadyScanned) {
        setScanningError({
          error: errorString.alreadyFound,
          severity: "info",
        });
        return prev;
      }

      const updatedListOfScannedQr = [...prev, qrValue];

      setScanningError({
        error: errorString.new,
        severity: "success",
      });

      onScannedListChange(updatedListOfScannedQr, qrValue);

      return updatedListOfScannedQr;
    });
  };
  return (
    <>
      <Dialog open={qrScannerToggle} onClose={setQrScannerToggleFalse}>
        <Box
          sx={{
            width: "100%",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            p: 2,
          }}
        >
          <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
            <DialogTitle sx={{ p: 0 }}>
              {t("inventory.findProducts")}
            </DialogTitle>
            <Typography>
              {listOfScannedQr.length} {t("selected")}
            </Typography>
          </Box>
          <IconButton
            onClick={setQrScannerToggleFalse}
            sx={{
              p: 2,
              display: "flex",
              alignItems: "center",
              "&:hover": { cursor: "pointer" },
            }}
          >
            <CloseRounded />
          </IconButton>
        </Box>

        <QrScanner handleScan={handleScan} />

        {scanningError.error && scanningError.severity && (
          <Alert variant="filled" severity={scanningError.severity}>
            {scanningError.error}
          </Alert>
        )}

        {scanningError.error === errorString.noExist ? (
          <Button
            variant="contained"
            onClick={() => {
              if (scanningError.serial)
                try {
                  AddProduct({ serial: scanningError.serial });
                } catch (error) {
                  if (error instanceof Error) {
                    switch (error.message) {
                      case ErrorCodes.MissingSerialField:
                        showSnackbar(t("errors.MissingSerialField"), "error");
                        break;
                      case ErrorCodes.SerialNotNumber:
                        showSnackbar(t("errors.SerialNotNumber"), "error");
                        break;
                      case ErrorCodes.SerialLength:
                        showSnackbar(t("errors.SerialLength"), "error");
                        break;
                      case ErrorCodes.BadBatchNumber:
                        showSnackbar(t("errors.BadBatchNumber"), "error");
                        break;
                      case ErrorCodes.ProductTypeNotFound:
                        showSnackbar(t("errors.ProductTypeNotFound"), "error");
                        break;
                      case ErrorCodes.BadSize:
                        showSnackbar(t("errors.BadSize"), "error");
                        break;
                      case ErrorCodes.NotASide:
                        showSnackbar(t("errors.NotASide"), "error");
                        break;
                      case ErrorCodes.ProductAlreadyExist:
                        showSnackbar(t("errors.ProductAlreadyExist"), "error");
                        break;
                      default:
                        showSnackbar(t("errors.UnexpectedError"), "error");
                        break;
                    }
                  } else {
                    showSnackbar(t("errors.UnexpectedError"), "error");
                  }
                }
            }}
          >
            {t("inventory.addProduct")}
          </Button>
        ) : (
          <></>
        )}
      </Dialog>
    </>
  );
};

export default QrScannerFind;
